import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const LongReadMoreContent = ({
  fullContent = "",
  floatDirection = "right",
  ctas = [],
  enableReadMore = true,
  scrollToTop = () => {}
}) => {
  const [isExpanded, setIsExpanded] = useState(!enableReadMore);
  const [showReadMore, setShowReadMore] = useState(enableReadMore);
  const readMoreContainer = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Check if the screen is mobile

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      toggleContent();
    }
  };

  // Toggle the Read More content
  const toggleContent = () => {
    setIsExpanded(!isExpanded);

    // Set a timeout to scroll to the top of the read more container
    setTimeout(() => {
      if (isExpanded) {
        if (scrollToTop) {
          scrollToTop();
        }
      }
    }, 400); // 400ms is the duration of the animation
  };
  // Set the content to hidden, then show it after the animation is done. This allows the height to be calculated correctly. And also allows the content to wrap properly for image right and image left variations
  useEffect(() => {
    if (readMoreContainer.current) {
      if (isExpanded) {
        readMoreContainer.current.style.visibility = "hidden";
      }
      readMoreContainer.current.style.maxHeight = isExpanded
        ? `${readMoreContainer.current.scrollHeight}px`
        : "0px";
      // set a timeout to hide the content after the animation is done

      setTimeout(() => {
        readMoreContainer.current.style.maxHeight = isExpanded
          ? `${readMoreContainer.current.scrollHeight}px`
          : "0px";
      }, 100);

      setTimeout(() => {
        if (isExpanded) {
          readMoreContainer.current.style.visibility = `visible`;
        } else {
          readMoreContainer.current.style.visibility = `hidden`;
        }
      }, 350);
    }
  }, [isExpanded]);

  // Check if the screen is mobile
  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  if (!enableReadMore) {
    return (
      <div>
        {fullContent}
        {ctas.length > 0 && (
          <div className="card__buttons-wrapper">
            {ctas.map((cta, index) => (
              <a
                key={index}
                href={cta.cta_destination}
                className={index === 0 ? "btn btn-primary" : "btn btn-ghost-orange"}
              >
                {cta.cta_label}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {showReadMore && !isExpanded && (
        <div className={`read-more__button-wrapper ${floatDirection}`} onKeyDown={handleKeyDown}>
          <a
            type="button"
            className="btn btn-text btn-text-blue"
            aria-expanded={!isExpanded}
            onClick={toggleContent}
            tabIndex={0}
          >
            Read More
          </a>
        </div>
      )}
      <div
        ref={readMoreContainer}
        className={`read-more__container ${
          isExpanded ? "read-more--is-open" : "read-more--is-closed"
        }`}
      >
        {fullContent}
        {ctas.length > 0 && (
          <div className="card__buttons-wrapper">
            {ctas.map((cta, index) => (
              <a
                key={index}
                href={cta.cta_destination}
                className={index === 0 ? "btn btn-primary" : "btn btn-ghost-orange"}
              >
                {cta.cta_label}
              </a>
            ))}
          </div>
        )}
        {isExpanded && (
          <div
            className="read-more__button-wrapper"
            onKeyDown={handleKeyDown}
            onClick={toggleContent}
          >
            <a
              type="button"
              className="btn btn-text btn-text-blue"
              aria-expanded={!isExpanded}
              tabIndex={0}
            >
              Read Less
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

LongReadMoreContent.propTypes = {
  fullContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  floatDirection: PropTypes.string,
  ctas: PropTypes.arrayOf(
    PropTypes.shape({
      cta_label: PropTypes.string,
      cta_destination: PropTypes.string
    })
  ),
  enableReadMore: PropTypes.bool,
  scrollToTop: PropTypes.func
};

export default LongReadMoreContent;
